import { FunctionComponent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Dispatch, RootState } from "core/store/store.types";
import {
  Alert,
  Avatar,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SocialDashboard } from "containers/social-dashboard";
import { MetaLogin } from "components/organisms/meta-login";
import { Media, Pricing, Profile } from "core/types/social.types";
import {
  socialActions,
  socialAsync,
  socialSelectors,
} from "core/store/modules/social";
import { FacebookUser } from "core/types/facebook-user.types";
import {
  facebookUserAsync,
  facebookUserSelectors,
} from "core/store/modules/facebook-user";
import { Text } from "components/atoms/text";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { customOrange } from "core/theme/variants";
import { IgAccountInfo } from "components/molecules/ig-account-info";
import { NoPostsEmptyState } from "components/molecules/no-posts-empty-state";
import useSessionStorageWatcher from "core/hooks/useSessionStorageWatcher";
import FB from "../../assets/svg/fb.svg";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface OwnProps {}
interface StateProps {
  isPostLoading: boolean;
  postList?: Media[];
  isPricingLoading: boolean;
  pricingObject?: Pricing;
  reportSuccess?: string;
  reportError?: string;
  facebookUser?: FacebookUser;
  profileList?: Profile[];
  isProfilesLoading: boolean;
  postError?: string;
}
interface DispatchProps {
  getPostList: (instagramId: string, page: number) => void;
  calculatePricing: (instagramId: string, posts: string[]) => void;
  sendRequest: (
    posts: string[],
    pricingRequestId: string,
    igPageId: string,
    level: number,
  ) => void;
  resetReportError: () => void;
  resetReportSuccess: () => void;
  saveFacebookInfo: (userInfo: any) => void;
  getFacebookInfo: () => void;
  getIGProfiles: () => void;
  resetPostError: () => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const Social: FunctionComponent<Props> = ({
  isPostLoading,
  postList,
  isPricingLoading,
  pricingObject,
  reportError,
  reportSuccess,
  facebookUser,
  profileList,
  isProfilesLoading,
  postError,
  getPostList,
  calculatePricing,
  sendRequest,
  resetReportError,
  saveFacebookInfo,
  getFacebookInfo,
  getIGProfiles,
  resetPostError,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [auth, setAuth] = useState(false);
  const sessionData = useSessionStorageWatcher("Meta_Token");
  const [selectedProfile, setSelectedProfile] = useState<Profile>();
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(false);

  useEffect(() => {
    if (auth && selectedProfile) {
      getPostList(selectedProfile.id, 1);
      setPage(1);
      setLastPage(false);
    }
  }, [auth, selectedProfile]);

  useEffect(() => {
    if (selectedProfile) {
      getPostList(selectedProfile.id, page);
    }
  }, [page]);

  useEffect(() => {
    if (postError) {
      setLastPage(true);
      setPage(page - 1);
      resetPostError();
    }
  }, [postError]);

  useEffect(() => {
    if (reportError) {
      setOpen(true);
      resetReportError();
    }
  }, [reportError]);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (auth) {
      getFacebookInfo();
    }
  }, [auth]);

  useEffect(() => {
    if (facebookUser) {
      getIGProfiles();
    }
  }, [facebookUser]);

  const handleMetaLogout = () => {
    sessionStorage.removeItem("Meta_Token");
    sessionStorage.removeItem("Facebook_User");
  };

  useEffect(() => {
    setAuth(sessionData ? true : false);
  }, [sessionData]);

  return (
    <>
      <Grid container display={"flex"} justifyContent={"center"}>
        {auth ? (
          <>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                ml={"20px"}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                    }}
                  >
                    <Avatar
                      alt={facebookUser?.name}
                      src={facebookUser?.picture.data.url}
                    />
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        bottom: "-5px",
                      }}
                    >
                      <img src={FB} width={"17px"} height={"17px"} />
                    </div>
                  </div>
                  <Text ml={"10px"}>{facebookUser?.name}</Text>
                </div>
                <Button variant="contained" onClick={() => handleMetaLogout()}>
                  {t("drawer-dashboard.logout")}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Paper
                  sx={{
                    borderRadius: "20px",
                    padding: "20px",
                  }}
                >
                  <FormControl
                    fullWidth
                    variant="standard"
                    sx={{ marginBottom: "26px" }}
                  >
                    <InputLabel
                      id="accounts-label"
                      sx={{ fontSize: "14px !important", fontWeight: "700" }}
                    >
                      {t("social-dashboard.accounts")}
                    </InputLabel>
                    <Select
                      labelId="accounts-label"
                      id="accounts-select"
                      value={selectedProfile ? selectedProfile?.name : ""}
                      MenuProps={MenuProps}
                      color="secondary"
                      renderValue={(value) => <Text>{value}</Text>}
                    >
                      {isProfilesLoading ? (
                        <MenuItem
                          disabled
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress size={24} />
                        </MenuItem>
                      ) : (
                        profileList?.map((p) => (
                          <MenuItem
                            key={p.id}
                            value={p.name}
                            onClick={() => setSelectedProfile(p)}
                          >
                            <ListItem>
                              <ListItemAvatar>
                                {p.profilePictureUrl ? (
                                  <Avatar
                                    src={p.profilePictureUrl}
                                    alt={p.username}
                                  />
                                ) : (
                                  <Avatar sx={{ bgcolor: customOrange[500] }}>
                                    <PersonRoundedIcon
                                      sx={{
                                        color: "white",
                                      }}
                                    />
                                  </Avatar>
                                )}
                              </ListItemAvatar>
                              <ListItemText
                                primary={p.name}
                                secondary={"@" + p.username}
                              />
                            </ListItem>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  {selectedProfile ? (
                    <>
                      <IgAccountInfo profile={selectedProfile} />

                      <SocialDashboard
                        instagramId={selectedProfile.id}
                        postList={postList}
                        isPostLoading={isPostLoading}
                        isPricingLoading={isPricingLoading}
                        pricingObject={pricingObject}
                        reportSuccess={reportSuccess}
                        page={page}
                        lastPage={lastPage}
                        setPage={setPage}
                        calculatePrice={calculatePricing}
                        sendRequest={sendRequest}
                      />
                    </>
                  ) : (
                    <NoPostsEmptyState />
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        ) : (
          <MetaLogin setAuth={setAuth} onFacebookCallback={saveFacebookInfo} />
        )}
      </Grid>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
          {reportError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    isPostLoading: socialSelectors.isPostLoading(state),
    postList: socialSelectors.getPostList(state),
    isPricingLoading: socialSelectors.isPricingLoading(state),
    pricingObject: socialSelectors.getPricingObject(state),
    reportError: socialSelectors.getReportError(state),
    reportSuccess: socialSelectors.getReportSuccess(state),
    facebookUser: facebookUserSelectors.getFacebookUser(state),
    profileList: socialSelectors.getProfileList(state),
    isProfilesLoading: socialSelectors.isProfilesLoading(state),
    postError: socialSelectors.getPostError(state),
  }),
  (dispatch: Dispatch) => ({
    getPostList: (instagramId: string, page: number) =>
      dispatch(socialAsync.getPostList(instagramId, page)),
    calculatePricing: (instagramId: string, posts: string[]) =>
      dispatch(socialAsync.calculatePricing(instagramId, posts)),
    sendRequest: (
      posts: string[],
      pricingRequestId: string,
      igPageId: string,
      level: number,
    ) => dispatch(socialAsync.requestReport(posts, pricingRequestId, igPageId, level)),
    resetReportSuccess: () =>
      dispatch(socialActions.reportSuccess({ success: "" })),
    resetReportError: () => dispatch(socialActions.reportError({ error: "" })),
    saveFacebookInfo: (userInfo: any) =>
      dispatch(facebookUserAsync.saveFacebookInfo(userInfo)),
    getFacebookInfo: () => dispatch(facebookUserAsync.getFacebookInfo()),
    getIGProfiles: () => dispatch(socialAsync.getIGProfiles()),
    resetPostError: () => dispatch(socialActions.postError({ error: "" })),
  })
)(Social);
